/**
 * [Gurumi 링크 미리보기]
 * 역할: yesmoney EditorLinkPreview 포팅
 * 작성/수정일: 2026-07-29
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { escapeAttr, escapeHtml } from '../utils.js';
import { resolveEditorImageUrl } from '../image-upload.js';

function resolveAlignment(value) {
  return value === 'left' || value === 'right' || value === 'center' ? value : 'center';
}

export function resolveLinkPreviewImageUrl(image) {
  const trimmed = String(image ?? '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return resolveEditorImageUrl(trimmed);
}

export function buildLinkPreviewHtml(attrs) {
  const href = String(attrs.href ?? '').trim();
  const title = String(attrs.title ?? '').trim() || '링크 미리보기';
  const description = String(attrs.description ?? '').trim();
  const image = resolveLinkPreviewImageUrl(String(attrs.image ?? ''));
  const domain = String(attrs.domain ?? '').trim();
  const alignment = resolveAlignment(attrs.alignment);
  const imageHtml = image
    ? `<img class="editor-link-preview__image" src="${escapeAttr(image)}" alt="" loading="lazy" />`
    : '';
  const descHtml = description ? `<div class="editor-link-preview__desc">${escapeHtml(description)}</div>` : '';
  const domainHtml = domain ? `<div class="editor-link-preview__domain">${escapeHtml(domain)}</div>` : '';
  return (
    `<div class="editor-link-preview" data-link-preview="" data-url="${escapeAttr(href)}" data-align="${alignment}"` +
    ` data-title="${escapeAttr(title)}" data-description="${escapeAttr(description)}" data-image="${escapeAttr(image)}" data-domain="${escapeAttr(domain)}">` +
    `<a class="editor-link-preview__card" href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">` +
    imageHtml +
    `<div class="editor-link-preview__body"><div class="editor-link-preview__title">${escapeHtml(title)}</div>${descHtml}${domainHtml}</div></a></div>`
  );
}

export const EditorLinkPreview = Node.create({
  name: 'linkPreview',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      href: { default: '', parseHTML: (el) => el.getAttribute('data-url') || el.querySelector('a')?.getAttribute('href') || '' },
      title: { default: '', parseHTML: (el) => el.getAttribute('data-title') || el.querySelector('.editor-link-preview__title')?.textContent?.trim() || '' },
      description: { default: '', parseHTML: (el) => el.getAttribute('data-description') || el.querySelector('.editor-link-preview__desc')?.textContent?.trim() || '' },
      image: { default: '', parseHTML: (el) => el.getAttribute('data-image') || el.querySelector('img')?.getAttribute('src') || '' },
      domain: { default: '', parseHTML: (el) => el.getAttribute('data-domain') || el.querySelector('.editor-link-preview__domain')?.textContent?.trim() || '' },
      alignment: { default: 'center', parseHTML: (el) => resolveAlignment(el.getAttribute('data-align')) },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-link-preview]' }, { tag: 'div.editor-link-preview' }];
  },

  renderHTML({ node }) {
    const alignment = resolveAlignment(node.attrs.alignment);
    const href = String(node.attrs.href ?? '');
    const title = String(node.attrs.title ?? '') || '링크 미리보기';
    const description = String(node.attrs.description ?? '');
    const image = resolveLinkPreviewImageUrl(String(node.attrs.image ?? ''));
    const domain = String(node.attrs.domain ?? '');
    const cardChildren = [];
    if (image) cardChildren.push(['img', { class: 'editor-link-preview__image', src: image, alt: '', loading: 'lazy' }]);
    const bodyChildren = [['div', { class: 'editor-link-preview__title' }, title]];
    if (description) bodyChildren.push(['div', { class: 'editor-link-preview__desc' }, description]);
    if (domain) bodyChildren.push(['div', { class: 'editor-link-preview__domain' }, domain]);
    cardChildren.push(['div', { class: 'editor-link-preview__body' }, ...bodyChildren]);
    return [
      'div',
      mergeAttributes({
        class: 'editor-link-preview',
        'data-link-preview': '',
        'data-url': href,
        'data-align': alignment,
        'data-title': title,
        'data-description': description,
        'data-image': image,
        'data-domain': domain,
      }),
      ['a', { class: 'editor-link-preview__card', href, target: '_blank', rel: 'noopener noreferrer' }, ...cardChildren],
    ];
  },

  addCommands() {
    return {
      insertLinkPreview:
        (attrs) =>
        ({ chain }) =>
          chain()
            .insertContent({
              type: this.name,
              attrs: {
                href: String(attrs.href ?? ''),
                title: String(attrs.title ?? ''),
                description: String(attrs.description ?? ''),
                image: String(attrs.image ?? ''),
                domain: String(attrs.domain ?? ''),
                alignment: resolveAlignment(attrs.alignment),
              },
            })
            .run(),
    };
  },

  addNodeView() {
    return ({ node: initialNode, editor, getPos }) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'editor-link-preview-node';
      wrapper.contentEditable = 'false';
      let node = initialNode;

      /**
       * [링크 미리보기]
       * 역할: alignment → data-align (플렉스 정렬 CSS와 연동)
       * 작성/수정일: 2026-07-29
       */
      const applyAlign = (alignment) => {
        const next = resolveAlignment(alignment);
        wrapper.dataset.align = next;
        wrapper.setAttribute('data-align', next);
      };

      const selectThis = () => {
        if (!editor.isEditable) return;
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (typeof pos === 'number') editor.commands.setNodeSelection(pos);
      };

      const render = (current) => {
        applyAlign(current.attrs.alignment);
        wrapper.innerHTML = buildLinkPreviewHtml({
          href: String(current.attrs.href ?? ''),
          title: String(current.attrs.title ?? ''),
          description: String(current.attrs.description ?? ''),
          image: String(current.attrs.image ?? ''),
          domain: String(current.attrs.domain ?? ''),
          alignment: resolveAlignment(current.attrs.alignment),
        });
        wrapper.querySelector('a.editor-link-preview__card')?.addEventListener('click', (event) => {
          if (editor.isEditable) {
            event.preventDefault();
            selectThis();
          }
        });
      };

      // [링크 미리보기] 역할: 카드 바깥 클릭도 노드 선택 — 작성/수정일: 2026-07-29
      wrapper.addEventListener('mousedown', (event) => {
        if (!editor.isEditable) return;
        if (event.button !== 0) return;
        selectThis();
      });

      render(node);
      return {
        dom: wrapper,
        update: (updated) => {
          if (updated.type.name !== 'linkPreview') return false;
          const nextAlign = resolveAlignment(updated.attrs.alignment);
          const sameContent =
            String(updated.attrs.href ?? '') === String(node.attrs.href ?? '') &&
            String(updated.attrs.title ?? '') === String(node.attrs.title ?? '') &&
            String(updated.attrs.description ?? '') === String(node.attrs.description ?? '') &&
            String(updated.attrs.image ?? '') === String(node.attrs.image ?? '') &&
            String(updated.attrs.domain ?? '') === String(node.attrs.domain ?? '');
          node = updated;
          if (sameContent) {
            applyAlign(nextAlign);
            return true;
          }
          render(updated);
          return true;
        },
        // [링크 미리보기] 역할: 선택 시 하이라이트 클래스 — 작성/수정일: 2026-07-29
        selectNode: () => {
          wrapper.classList.add('ProseMirror-selectednode', 'is-selected');
        },
        deselectNode: () => {
          wrapper.classList.remove('ProseMirror-selectednode', 'is-selected');
        },
        ignoreMutation: () => true,
      };
    };
  },
});
